import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaBuilding, FaSearch } from "react-icons/fa";
import useU2000SelectOrganization from "../../hooks/authentification/U2000UseSelectOrganization";
import { useU2000AuthContext } from "../../context/auth/U2000UseAuthContext";
import { useNavigate } from "react-router-dom";
import { u2000Menu } from "../../menu/U2000Menu";
import useU2000OrganizationPersistence from "../../hooks/authentification/U2000UseOrganizationPersistence";
import { useToast } from "../../context/toast/U2000ToastContext";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const U2000SelectOrganizationDialog: React.FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { auth } = useU2000AuthContext();
  const toast = useToast();
  const doSelectOrganization = useU2000SelectOrganization();
  const { persistedOrganization, persistOrganization, clearPersistedOrganization } = useU2000OrganizationPersistence();
  const [isLoading, setIsLoading] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Check if current organization is persisted
  const isCurrentOrgPersisted = persistedOrganization?.uuid === auth?.user?.organizationId;

  const handleToggleRemember = () => {
    if (isCurrentOrgPersisted) {
      // Forget current organization
      clearPersistedOrganization();
      toast.success(t("organizationForgotten"), t("successTitle"), 3000);
    } else {
      // Remember current organization
      if (auth?.user?.organizationId && auth?.user?.organizationName) {
        persistOrganization(auth.user.organizationId, auth.user.organizationName);
        toast.success(t("organizationRemembered"), t("successTitle"), 3000);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!isOpen) return;
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Auto-focus on search input when dialog opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleSelect = async (org: any) => {
    setErrMsg(null);
    setIsLoading(true);
    try {
      // Use current token from auth context
      const response = await doSelectOrganization(org.uuid, u2000Menu);
      if (response && response.isAuthenticated) {
        toast.success(t("organizationSwitched", { organizationName: org.organizationName }), t("successTitle"), 3000);

        onClose();
        navigate("/", { replace: true });
        // Avoid full page reload to preserve client-side state/localStorage
        // window.location.reload();
      }
    } catch (error: any) {
      if (error.status === 404) {
        setErrMsg(t("organizationNotFound"));
        toast.error(t("organizationNotFound"), t("errorTitle"), 5000);
      } else {
        setErrMsg(t("msgUnexpectedError"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const list = (auth?.user?.organizations || []).filter((o) => {
    // Exclude the currently selected organization from the list
    const isCurrent = auth?.user?.organizationId && o.uuid === auth.user.organizationId;
    if (isCurrent) return false;
    return (o.organizationName || "").toLowerCase().includes(filter.trim().toLowerCase());
  });

  if (!isOpen) return null;

  return (
    <div className="fixed left-14 top-6 z-50">
      <div ref={ref} className="w-[320px] bg-white rounded-lg shadow-2xl overflow-hidden" role="dialog" aria-modal="true" aria-labelledby="dialog-title" aria-describedby="dialog-description">
        <div className="flex items-center justify-between bg-gray-800 text-white px-4 py-3">
          <div className="flex items-center space-x-3 min-w-0">
            <div className="bg-green-600 p-1.5 rounded-lg shrink-0">
              <img src="/assets/MonArpenteurWhite.svg" alt="MonArpenteur" className="w-6 h-6 rounded-sm object-contain" />
            </div>
            <div className="flex flex-col min-w-0">
              <span id="dialog-title" className="text-white font-semibold text-sm flex-1 truncate min-w-0">
                {auth?.user?.organizationName || auth?.user?.organizationId || ""}
              </span>
            </div>
          </div>
        </div>

        {/* Toggle Remember Button */}
        <div className="px-4  bg-gray-900">
          <button
            onClick={handleToggleRemember}
            disabled={isLoading}
            aria-pressed={isCurrentOrgPersisted}
            aria-label={isCurrentOrgPersisted ? t("btnForget") : t("btnRemember")}
            className={`w-full text-center text-sm py-2 px-3 rounded-lg transition-all duration-200
                        focus:outline-hidden focus:ring-2 focus:ring-offset-2 
                        disabled:opacity-50 disabled:cursor-not-allowed text-black font-medium ${
                          isCurrentOrgPersisted
                            ? "bg-orange-300 hover:bg-orange-400 border border-orange-400 focus:ring-orange-500/50"
                            : "bg-blue-300 hover:bg-blue-400 border border-blue-400 focus:ring-blue-500/50"
                        }`}
          >
            {" "}
            {isCurrentOrgPersisted ? t("btnForget") : t("btnRemember")}
          </button>
        </div>

        <div className="p-4 bg-gray-900">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              ref={inputRef}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t("lblFilterOrganization")}
              aria-label={t("lblFilterOrganization")}
              role="searchbox"
              disabled={isLoading}
              className="w-full h-[46px] pl-10 pr-4 py-3 rounded-lg bg-gray-800 text-white text-sm 
                         border border-gray-700 transition-all duration-200 focus:outline-hidden 
                         focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 
                         placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        <div className="p-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
              <span className="ml-3 text-sm text-gray-600">{t("lblLoading")}</span>
            </div>
          ) : list.length === 0 ? (
            <div className="text-center py-8">
              <FaBuilding className="mx-auto text-gray-400 text-2xl mb-3" />
              <div className="text-sm text-gray-500">{filter ? t("lblNoOrganizationsFound", { ns: "common" }) : t("lblNoOrganizationsAvailable")}</div>
            </div>
          ) : (
            <div className="max-h-[300px] overflow-auto pr-2">
              <ul className="space-y-2">
                {list.map((org: any) => (
                  <li key={org.uuid}>
                    <button
                      onClick={() => handleSelect(org)}
                      disabled={isLoading}
                      aria-label={`${t("lblSelectOrganization", { ns: "common" })} ${org.organizationName}`}
                      className="w-full flex items-center gap-3 rounded px-3 py-2 hover:bg-gray-100 text-left
                                 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent
                                 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 focus:bg-gray-100
                                 transition-all duration-200"
                    >
                      <FaBuilding className="text-gray-700" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-800">{org.organizationName}</div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {errMsg && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="text-sm text-red-600 font-medium">{errMsg}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default U2000SelectOrganizationDialog;
